<script setup lang="ts">
  import { StorageSerializers, useStorage } from '@vueuse/core';
  import { storeToRefs } from 'pinia';
  import { computed, onBeforeMount, ref } from 'vue';
  import { RouterLink } from 'vue-router';

  import type { Email, EmailThread, Partner } from '@stuut/api/models';
  import { formatPhoneNumber } from '@stuut/common/src/utils';
  import { Badge } from '@ui/components/badge';
  import { MonetaryText } from '@ui/components/monetary';
  import { Textarea } from '@ui/components/textarea';
  import { Timestamp } from '@ui/components/time';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { stuut } from '@/api';
  import { EmailThreadDrawer } from '@/components/email-threads';
  import TaskReplyWarning from '@/components/tasks/TaskReplyWarning.vue';
  import TaskTitle from '@/components/tasks/TaskTitle.vue';
  import { MAX_NOTE_LENGTH } from '@/constants/CustomerNotes';
  import { useTaskCollectionStore } from '@/store';

  const emits = defineEmits<{
    (e: 'close'): void;
    (e: 'openConfirmComplete'): void;
    (e: 'enableCloseOnRouteChange'): void;
  }>();

  const taskCollectionStore = useTaskCollectionStore();
  const { currentTask } = storeToRefs(taskCollectionStore);

  const showEmailThread = ref(false);
  const callNotes = useStorage(
    `call-notes-${currentTask?.value?.id}`,
    { init: false, initialValue: '', note: '' },
    undefined,
    {
      serializer: StorageSerializers.object,
    },
  );

  const emailId = ref<string | undefined>(currentTask?.value?.emailData?.id);
  const emailThreadId = ref<string | undefined>(currentTask?.value?.emailData?.thread_id);

  const partnerId = ref<string | null>(currentTask?.value?.partner_id ?? null);

  const remainingCharacters = computed(
    () => MAX_NOTE_LENGTH - (callNotes.value?.note?.length ?? 0),
  );

  const email = ref<Email | null>(null);
  const emailThread = ref<EmailThread | null>(null);
  const partner = ref<Partner | null>(null);
  const loadingEmail = ref<boolean>(false);

  const loadEmail = async () => {
    loadingEmail.value = true;

    try {
      email.value = await stuut?.emails?.retrieve(emailId.value);

      if (emailThreadId.value) {
        emailThread.value = await stuut.emailThreads.retrieve(emailThreadId.value);
      }

      if (partnerId.value) {
        partner.value = await stuut?.partners?.retrieve(partnerId.value);
      }
    } catch (error) {
      console.error(error);
    } finally {
      loadingEmail.value = false;
    }
  };

  const workflowStage = computed(() => currentTask?.value?.outreach_stage_run?.stage);

  const cadenceLabel = computed<string>(() => {
    if (workflowStage.value?.time_trigger === 'on' || !workflowStage.value?.num_days) {
      return workflowStage.value?.event === 'invoice.due_date' ? 'due' : 'issued';
    }

    const days = `${workflowStage.value?.num_days} day${workflowStage.value?.num_days === 1 ? '' : 's'}`;

    if (workflowStage.value?.time_trigger === 'before') {
      return workflowStage.value?.event === 'invoice.due_date'
        ? `${days} until due date`
        : `${days} until finalized`;
    }

    return workflowStage.value?.event === 'invoice.due_date'
      ? `${days} past due`
      : `${days} after being finalized`;
  });

  const isNotesDirty = computed(
    () => callNotes.value.note?.trim() !== callNotes.value.initialValue?.trim(),
  );

  const sourceIcon = computed(() => {
    switch (currentTask?.value?.source) {
      case 'call':
        return 'fa-phone-arrow-up-right text-3xs!';
      case 'email':
        return 'fa-inbox-in text-xs!';
      default:
        return '';
    }
  });

  onBeforeMount(async () => {
    if (!callNotes.value.init && !callNotes.value.note) {
      callNotes.value.note = `Manual Call - Invoice ${cadenceLabel.value} - ${currentTask?.value?.created_at?.format('M/D/YY')}\n\n`;

      callNotes.value.initialValue = callNotes.value.note;
      callNotes.value.init = true;
    }

    await loadEmail();
  });

  defineExpose({
    isNotesDirty: computed(() => isNotesDirty.value),
    notes: computed(() => callNotes.value.note),
    partner,
  });
</script>

<template>
  <div class="flex h-auto overflow-hidden flex-1 rounded">
    <div class="h-full w-full space-y-8">
      <div class="space-y-4">
        <TaskTitle :task="currentTask" :partner="partner as any">
          <template #title> Call {{ partner?.name }} </template>
          <template #tooltip_content> Call {{ partner?.name }} </template>
        </TaskTitle>

        <div class="bg-secondary p-5 rounded space-y-6">
          <div class="w-full grid grid-cols-[120px_1fr] items-center gap-2.5">
            <span>Task Trigger</span>
            <div class="flex items-center gap-x-1.5">
              <template v-if="currentTask?.isBrokenPromiseFollowUp">
                <span class="font-semibold space-x-2">
                  <i class="fas fa-heart-crack" />
                  <span>Broke Payment Promise</span>
                </span>
              </template>

              <template
                v-else-if="
                  currentTask?.taskComponentType === 'email_response' ||
                  currentTask?.taskComponentType === 'acknowledge'
                "
              >
                <span class="font-semibold space-x-1.5">
                  <i class="fa-solid" :class="sourceIcon"></i>

                  <span v-if="currentTask?.isEmailSource">{{ (email?.to ?? []).join(', ') }}</span>

                  <span v-if="currentTask?.isCallSource" class="capitalize">{{
                    formatPhoneNumber(currentTask?.callData?.to_number ?? '')
                  }}</span>
                </span>
              </template>

              <template v-else-if="currentTask?.taskComponentType === 'manual_call'">
                <span class="font-semibold space-x-2">
                  <i class="far fa-calendar" />
                  <span>Invoice {{ cadenceLabel }}</span>
                </span>
              </template>
            </div>
            <span>Date</span>
            <div class="space-x-1.5">
              <span class="font-semibold">
                <Timestamp
                  v-if="currentTask?.created_at"
                  :with-conversion-table="true"
                  :time="currentTask?.created_at"
                  local
                  :show-from-now="true"
                  format="MMM DD, YYYY"
                />

                {{ currentTask?.callData?.start_time?.local()?.format('h:mma') }}
              </span>
            </div>
            <span class="text-xs">Open Invoices</span>
            <div class="space-x-1.5">
              <strong>{{ partner?.num_open_invoices }}</strong>
              <RouterLink
                v-if="partner?.id"
                :to="{
                  name: 'customers.customer.invoices',
                  params: { id: partner?.id },
                  // @ts-ignore
                  query: { istatus: { in: ['open', 'overdue'] } },
                }"
                class="text-blue hover:underline"
                @click="emits('enableCloseOnRouteChange')"
                >view</RouterLink
              >
            </div>

            <span>Total Overdue</span>
            <div class="space-x-1.5">
              <MonetaryText class="font-bold" :value="partner?.total_overdue || 0" />
            </div>

            <span>Total Outstanding</span>
            <MonetaryText class="font-bold" :value="partner?.invoice_balance || 0" />

            <span>Last activity</span>
            <strong
              >{{ partner?.last_outreach_at?.format('MMM D') ?? '--' }}
              {{
                partner?.last_outreach_channel ? `(${partner?.last_outreach_channel})` : ''
              }}</strong
            >
          </div>
          <TaskReplyWarning
            class="text-xs text-muted-foreground"
            :task="currentTask"
            :show-any-task-type="true"
          />
        </div>
      </div>

      <div
        v-if="partner?.activePhoneContacts?.length"
        class="flex flex-col gap-y-2.5"
        :class="!currentTask?.isOpen && 'opacity-50'"
      >
        <div class="flex items-center justify-between">
          <h5 class="font-bold text-muted-foreground">Contacts</h5>

          <div
            v-if="!partner?.primaryPhoneContact?.phone"
            class="text-destructive items-center gap-x-1 flex"
          >
            <Tooltip :delay-duration="0">
              <TooltipTrigger as-child>
                <div class="items-center gap-x-1.5 flex">
                  <i class="fa-solid fa-exclamation-circle"></i>
                  <div class="font-bold">WARNING:</div>
                  <div>
                    No primary
                    <span class="font-bold">phone contact set</span>.
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent class="max-w-[300px] space-y-2.5">
                <p>We currently cannot perform any call related outreach.</p>

                <p>
                  Set a
                  <span class="font-semibold"> Primary phone </span>
                  on the
                  <RouterLink
                    v-if="partner?.id"
                    :to="{
                      name: 'customers.customer',
                      params: { id: partner?.id },
                    }"
                    class="text-blue hover:underline"
                    @click="emits('enableCloseOnRouteChange')"
                    >customer page</RouterLink
                  >.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div class="w-full">
          <div
            v-for="(contact, idx) in partner?.allContacts"
            :key="contact?.id"
            class="grid grid-cols-3 items-center h-10"
            :class="{ 'border-t': idx !== 0 }"
          >
            <div class="flex items-center gap-x-4">
              <span>{{ contact.name }}</span>
            </div>

            <div class="flex items-center gap-x-2.5">
              <strong>{{ formatPhoneNumber(contact.phone) }}</strong>

              <Tooltip v-if="contact.is_primary_phone" :delay-duration="0">
                <TooltipTrigger as-child>
                  <Badge
                    variant="agent"
                    class="justify-center size-6 rounded-full text-center"
                    size="xs"
                  >
                    <i class="fas fa-phone" />
                  </Badge>
                </TooltipTrigger>

                <TooltipContent> Primary phone </TooltipContent>
              </Tooltip>
            </div>

            <div class="flex items-center justify-left gap-x-2">
              <span>{{ contact.email }}</span>

              <Tooltip v-if="contact.is_primary_email" :delay-duration="0">
                <TooltipTrigger as-child>
                  <Badge
                    variant="agent"
                    class="justify-center size-6 rounded-full text-center"
                    size="xs"
                  >
                    <i class="far fa-envelope-circle-check ml-1" />
                  </Badge>
                </TooltipTrigger>

                <TooltipContent> Primary email </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      <div class="text-muted-foreground space-y-4">
        <div class="flex items-center gap-x-1.5">
          <strong>Notes:</strong>

          <Tooltip :delay-duration="0">
            <TooltipTrigger as-child>
              <i class="fa far fa-info-circle text-2xs"></i>
            </TooltipTrigger>

            <TooltipContent>
              Call notes will be saved to the customer page when you complete the task.
            </TooltipContent>
          </Tooltip>
        </div>

        <Textarea
          v-model="callNotes.note"
          class="min-h-32"
          :disabled="!currentTask?.isOpen"
          :maxlength="MAX_NOTE_LENGTH"
        />

        <p>
          Max {{ MAX_NOTE_LENGTH }} characters ({{ remainingCharacters }}
          remaining)
        </p>
      </div>
    </div>

    <EmailThreadDrawer
      v-if="emailThread"
      v-model:open="showEmailThread"
      :thread="emailThread"
      is-slider
      default-open
      class="h-full min-h-full"
      @close="
        () => {
          emailThread = null;
          showEmailThread = false;
        }
      "
    />
  </div>
</template>

<style scoped></style>
